import { onChildAdded, set } from 'firebase/database';
import AdapterMobileVoidV1 from '~/class/AdapterMobileVoidV1';
import { useMobileV1Toast } from '~/composables/useMobileV1Toast';
import { readVoidPrintKitchen, readVoidCopyToCashier } from '~/utils/voidPrintPrefs';

// Void-from-mobile gateway — cashier-side answerer for the legacy
// `tables_v2_void_from_mobile` Firebase silo that the (unchanged) Java mobile
// client (VoidItemsActivity) uses to void items from a phone.
//
// Contract replicated from VoidActivity.java:
//   1. tables_v2_handshake_void_from_mobile : phone pushes a child, waits for it
//      to become `>= 2`. We flip 0|1 → 2 (auto-clear after 8s) — the phone's
//      "go ahead" / success ping (callbackPingSuccess).
//   2. tables_v2_void_from_mobile : phone pushes the void request JSON. We dedupe,
//      guard, convert it into a JobAction.VOID jobcard (AdapterMobileVoidV1) and
//      push it into the unified jobchain. The existing plugins then remove the
//      items, print the void slips and record the void to DynamoDB. We ack the
//      node with read:2 and clear it after 8s.
//
// Only the main cashier runs this (init is called from bootstrap's
// initCashierControllerRuntime, cashier-only — same as pos.mobile-v1).
export const usePosMobileVoidV1Store = defineStore("mobile-void-v1", () => {

  // dedupe by the pushed child key — the void payload carries no uniqueid.
  const processedKeys = new Set<string>()

  const handleHandshake = (snapshot: any) => {
    const val = snapshot.val()
    if (val === 0 || val === 1) {
      set(snapshot.ref, 2)
      setTimeout(() => { set(snapshot.ref, null) }, 8000)
    }
  }

  // Cross-tab dedupe window (ms). A given mobile-void node may only be processed
  // once across ALL tabs on this machine within this window (localStorage marker).
  const VOID_DEDUPE_MS = 10000
  const VOID_MARKER_PREFIX = '__void_done:'
  const voidMarkerKey = (key: string) => `${VOID_MARKER_PREFIX}${key}`

  // Sweep stale markers so they don't accumulate in localStorage forever (a tab
  // closed before its 10s cleanup setTimeout fired leaves its marker behind).
  // Called at init() on program start. Only removes EXPIRED / malformed markers —
  // a still-fresh marker (age < window) may belong to another tab mid-process, so
  // it's left alone.
  const sweepStaleVoidMarkers = () => {
    if (typeof window === 'undefined') return
    try {
      const now = Date.now()
      const toRemove: string[] = []
      for (let i = 0; i < window.localStorage.length; i++) {
        const k = window.localStorage.key(i)
        if (!k || !k.startsWith(VOID_MARKER_PREFIX)) continue
        const v = Number(window.localStorage.getItem(k))
        if (!v || now - v >= VOID_DEDUPE_MS) toRemove.push(k)
      }
      toRemove.forEach((k) => window.localStorage.removeItem(k))
    } catch (e) {
      // localStorage may be unavailable/blocked — non-fatal.
    }
  }

  // Has this snapshot already been claimed/processed by some tab in the window?
  // The marker value is the claim timestamp so a stale marker (e.g. winner tab
  // closed before its cleanup ran) self-expires instead of blocking forever.
  const isVoidClaimed = (key: string): boolean => {
    if (!key || typeof window === 'undefined') return false
    const prev = window.localStorage.getItem(voidMarkerKey(key))
    return !!prev && Date.now() - Number(prev) < VOID_DEDUPE_MS
  }
  const claimVoid = (key: string) => {
    if (key && typeof window !== 'undefined') window.localStorage.setItem(voidMarkerKey(key), String(Date.now()))
  }
  const releaseVoid = (key: string) => {
    if (key && typeof window !== 'undefined') window.localStorage.removeItem(voidMarkerKey(key))
  }

  const handleVoid = async (snapshot: any) => {
    const refPath = snapshot.ref
    const jsonString = snapshot.val()
    if (jsonString == null) return

    let payload: any
    try {
      payload = typeof jsonString === 'string' ? JSON.parse(jsonString) : jsonString
    } catch (e) {
      return
    }

    if (payload.read == 2) return

    const key = String(snapshot.key || '')
    if (key && processedKeys.has(key)) return
    // Fast path — already handled by some tab within the dedupe window.
    if (isVoidClaimed(key)) return

    // The actual processing. Gated by a cross-tab lock (below) so only ONE tab
    // runs it: the cashier PC commonly has 2 tabs open and both would otherwise
    // print + record the void twice (the /printcustom cashier copy has no
    // downstream UniqueId dedupe).
    const run = async () => {
      // Re-check inside the lock so the loser of a near-simultaneous race bails.
      if (isVoidClaimed(key)) return
      claimVoid(key)
      if (key) processedKeys.add(key)

      // Block while the shop is running over a closed business day — same guard as
      // pos.mobile-v1 / pos.job-gateway. Reject by removing + warn.
      if (usePosJobsStore().isRunningOverClosedDay()) {
        releaseVoid(key) // rejected, not done — let a future retry through
        setTimeout(() => { set(refPath, null) }, 2500)
        useMobileV1Toast().push({
          variant: 'warn',
          text: 'ร้านเปิดข้ามวัน — กรุณาปิดร้านแล้วเปิดใหม่ก่อนรับคำสั่งจากมือถือ',
          duration: 4000,
        })
        return
      }

      const { job, matchedCount } = new AdapterMobileVoidV1()
        .fromCloudToJobchain(typeof jsonString === 'string' ? jsonString : JSON.stringify(payload), key)

      if (!job || matchedCount === 0) {
        releaseVoid(key)
        useMobileV1Toast().push({
          variant: 'warn',
          text: `ไม่พบรายการที่จะ void — ${payload.uuid ?? ''}`,
          duration: 3000,
        })
        setTimeout(() => { set(refPath, null) }, 2500)
        return
      }



      job.pcTimestamp = useServerTime().nowISO();

      // Print like the Void modal's toggles on this terminal (kitchen / cashier copy).
      if (!readVoidPrintKitchen()) (job as any).__noPrint = true
      if (readVoidCopyToCashier()) (job as any).__copyToCashier = true


      // Prints + records the void automatically via the add-action plugins.
      usePosJobsStore().add(job, true)

      // Ack the phone — read:2 keeps the node from being reprocessed, then clear.
      payload.read = 2
      set(refPath, JSON.stringify(payload))
      setTimeout(() => { set(refPath, null) }, 8000)

      useMobileV1Toast().push({
        variant: 'success',
        text: `Void from mobile — ${job.alias}`,
        duration: 2000,
      })

      // Best-effort marker cleanup; the age-check in isVoidClaimed is the real guard.
      if (key) setTimeout(() => { releaseVoid(key) }, VOID_DEDUPE_MS)
    }

    // Web Locks picks the winner atomically (race-free). The loser gets lock=null
    // and skips. Fall back to marker-only dedupe on browsers without the API.
    if (key && typeof navigator !== 'undefined' && navigator.locks?.request) {
      await navigator.locks.request(`void_from_mobile:${key}`, { ifAvailable: true }, async (lock) => {
        if (!lock) return
        await run()
      })
    } else {
      await run()
    }
  }

  const init = () => {
    // Clear leftover dedupe markers from previous sessions on program start.
    sweepStaleVoidMarkers()

    const handshakeRef = useRealtimeDatabase().handshakeVoidFromMobileRef()
    set(handshakeRef, null)
    onChildAdded(handshakeRef, handleHandshake)

    const voidRef = useRealtimeDatabase().voidFromMobileRef()
    set(voidRef, null)
    onChildAdded(voidRef, handleVoid)
  }

  return {
    init,
  }
})
